import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { CurrencyInput } from '@components/core-ui/form-elements/CurrencyInput';
import { Input } from '@components/core-ui/form-elements/Input';
import { Select } from '@components/core-ui/form-elements/Select';
import { Switch } from '@components/core-ui/form-elements/Switch';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { EErrorMessages } from 'app/types/errorMessages';
import { useAPI } from '@providers/useAPI';
import { useModalContext } from '@providers/ModalProvider';
import { AdminGetAllAsonePlansPlanResponseDto, AdminService, EditASOnePlanBodyDto } from 'app/API';
import React, { useState } from 'react';
import toast from 'react-hot-toast';

import { Content } from './EditASOnePlanModal/styles/Content';

type BillingRateType = 'FLAT_FEE' | 'PER_HOUR' | 'TIER_RATE' | 'DISALLOWED';
type BillingFullUnlockChargeType = 'PAY_DIFFERENCE' | 'ADDITIONAL_CHARGE';

function isBillingRateType(v: string): v is BillingRateType {
  return v === 'FLAT_FEE' || v === 'PER_HOUR' || v === 'TIER_RATE' || v === 'DISALLOWED';
}
function isBillingFullUnlockChargeType(v: string): v is BillingFullUnlockChargeType {
  return v === 'PAY_DIFFERENCE' || v === 'ADDITIONAL_CHARGE';
}

type TProps = {
  plan: AdminGetAllAsonePlansPlanResponseDto;
  onCancel: () => void;
  onSuccess: () => void;
};

export const EditASOnePlanModal = ({ plan: initialPlan, onCancel, onSuccess }: TProps) => {
  const { call } = useAPI();
  const { closeModal } = useModalContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [price, setPrice] = useState(String(initialPlan.price));
  const [asrRate, setAsrRate] = useState(String(initialPlan.DefaultBillingConfiguration?.realTimeRate ?? '0'));
  const [postRate, setPostRate] = useState(String(initialPlan.DefaultBillingConfiguration?.postProcessingRate ?? '0'));
  const [reporterUnlockRate, setReporterUnlockRate] = useState(
    String(initialPlan.DefaultBillingConfiguration?.partialUnlockRate ?? '0'),
  );
  const [fullUnlockRate, setFullUnlockRate] = useState(
    String(initialPlan.DefaultBillingConfiguration?.fullUnlockRate ?? '0'),
  );
  const [enhancedOnDemandRate, setEnhancedOnDemandRate] = useState(
    String(initialPlan.DefaultBillingConfiguration?.enhancedOnDemandRate ?? '0'),
  );
  const [attorneyConnectFeeRate, setAttorneyConnectFeeRate] = useState(
    String(initialPlan.DefaultBillingConfiguration?.attorneyConnectFeeRate ?? '0'),
  );
  const [attorneyInviteFeeRate, setAttorneyInviteFeeRate] = useState(
    String(initialPlan.DefaultBillingConfiguration?.attorneyInviteFeeRate ?? '0'),
  );

  const [rushLabelFeeRate, setRushLabelFeeRate] = useState(
    String(initialPlan.DefaultBillingConfiguration?.rushLabelFeeRate ?? '0'),
  );

  const [plan, setPlan] = useState({
    ...initialPlan,
    description: initialPlan.description.split('<newLine>').join('\n'),

    asrRateType: initialPlan.DefaultBillingConfiguration?.realTimeRateType ?? 'FLAT_FEE',
    postRateType: initialPlan.DefaultBillingConfiguration?.postProcessingRateType ?? 'FLAT_FEE',
    reporterUnlockRateType: initialPlan.DefaultBillingConfiguration?.partialUnlockRateType ?? 'FLAT_FEE',
    fullUnlockRateType: initialPlan.DefaultBillingConfiguration?.fullUnlockRateType ?? 'FLAT_FEE',
    fullUnlockChargeType: initialPlan.DefaultBillingConfiguration?.fullUnlockChargeType ?? 'PAY_DIFFERENCE',
    enhancedOnDemandRateType: initialPlan.DefaultBillingConfiguration?.enhancedOnDemandRateType ?? 'FLAT_FEE',
    attorneyConnectFeeRateType: initialPlan.DefaultBillingConfiguration?.attorneyConnectFeeRateType ?? 'FLAT_FEE',
    attorneyInviteFeeRateType: initialPlan.DefaultBillingConfiguration?.attorneyInviteFeeRateType ?? 'FLAT_FEE',
    defaultBillingConfigurationId: initialPlan.defaultBillingConfigurationId || undefined,
    allowedToChooseTranscriptUnlockType: initialPlan.allowedToChooseTranscriptUnlockType || false,

    unlimitedHoursPerMonth: initialPlan.unlimitedHoursPerMonth || false,
    audioHoursPerMonth: initialPlan.audioHoursPerMonth ?? 0,
    rushLabelFeeRateType: initialPlan.DefaultBillingConfiguration?.rushLabelFeeRateType ?? 'FLAT_FEE',
  });

  const tierTypes = [
    { label: 'Flat Fee', value: 'FLAT_FEE' },
    { label: 'Fixed per hour rate', value: 'PER_HOUR' },
    { label: 'Tier Rate', value: 'TIER_RATE' },
    { label: 'Disallowed', value: 'DISALLOWED' },
  ];

  const fullUnlockChargeTypes = [
    { label: 'Pay difference', value: 'PAY_DIFFERENCE' },
    { label: 'Additional charge', value: 'ADDITIONAL_CHARGE' },
  ];

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const requestBody: EditASOnePlanBodyDto = {
        ...plan,
        realtimeASREnabled: plan.realtimeASREnabled,
        price: Number(price),
        asrRate: Number(asrRate),
        postRate: Number(postRate),
        reporterUnlockRate: Number(reporterUnlockRate),
        fullUnlockRate: Number(fullUnlockRate),
        enhancedOnDemandRate: Number(enhancedOnDemandRate),
        attorneyConnectFeeRate: Number(attorneyConnectFeeRate),
        attorneyInviteFeeRate: Number(attorneyInviteFeeRate),
        inPersonCaptureChannels: Number(plan.inPersonCaptureChannels),
        rushLabelFeeRate: Number(rushLabelFeeRate),
      };
      await call(
        AdminService.editAsOnePlan({
          subscriptionProductId: plan.id,
          requestBody,
        }),
      );
      setIsSubmitting(false);
      closeModal();
      onSuccess();
    } catch {
      setIsSubmitting(false);
      toast.error(EErrorMessages.DEFAULT);
    }
  };

  return (
    <ModalWrapper width="405px" overflow="auto">
      {isSubmitting ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Edit plan</ModalTitle>
      <Content>
        <div className="input-wrapper">
          <div className="label">Plan title</div>
          <Input
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPlan({ ...plan, name: e.target.value })}
            label="Plan title"
            value={plan.name}
          />
        </div>

        <div className="flex-row flex gap-2 items-top">
          <div className="input-wrapper">
            <div className="label">Monthly price per license</div>
            <CurrencyInput
              onChange={(_, v2) => {
                setPrice(v2);
              }}
              label="$ 0"
              value={price}
            />
          </div>

          <div className="input-wrapper">
            <div className="label">Realtime ASR Enabled</div>
            <Switch
              checked={plan.realtimeASREnabled}
              onChange={() => setPlan({ ...plan, realtimeASREnabled: !plan.realtimeASREnabled })}
            />
          </div>
        </div>

        <div className="flex-row flex gap-2 items-top">
          <div className="input-wrapper">
            <div className="label">Realtime / ASR rate</div>

            <Select
              name="asrRateType"
              label="Type"
              value={plan.asrRateType || 'FLAT_FEE'}
              onChange={(_, value) =>
                setPlan({ ...plan, asrRateType: isBillingRateType(value) ? value : plan.asrRateType })
              }
              options={tierTypes}
              required={false}
            />
            {plan.asrRateType !== 'TIER_RATE' && plan.asrRateType !== 'DISALLOWED' ? (
              <CurrencyInput
                onChange={(_, v2) => {
                  setAsrRate(v2);
                }}
                label="$ 0"
                value={asrRate}
              />
            ) : null}
          </div>
          <div className="input-wrapper">
            <div className="label">Post rate</div>
            <Select
              name="postRateType"
              label="Type"
              value={plan.postRateType || 'FLAT_FEE'}
              onChange={(_, value) =>
                setPlan({ ...plan, postRateType: isBillingRateType(value) ? value : plan.postRateType })
              }
              options={tierTypes}
              required={false}
            />
            {plan.postRateType !== 'TIER_RATE' && plan.postRateType !== 'DISALLOWED' ? (
              <CurrencyInput
                onChange={(_, v2) => {
                  setPostRate(v2);
                }}
                label="$ 0"
                value={postRate}
              />
            ) : null}
          </div>
        </div>

        <div className="flex-row flex gap-2 items-top">
          <div className="input-wrapper">
            <div className="label">Reporter Unlock rate</div>
            <Select
              name="reporterUnlockRateType"
              label="Type"
              value={plan.reporterUnlockRateType || 'FLAT_FEE'}
              onChange={(_, value) =>
                setPlan({
                  ...plan,
                  reporterUnlockRateType: isBillingRateType(value) ? value : plan.reporterUnlockRateType,
                })
              }
              options={tierTypes}
              required={false}
            />
            {plan.reporterUnlockRateType !== 'TIER_RATE' && plan.reporterUnlockRateType !== 'DISALLOWED' ? (
              <CurrencyInput
                onChange={(_, v2) => {
                  setReporterUnlockRate(v2);
                }}
                label="$ 0"
                value={reporterUnlockRate}
              />
            ) : null}
          </div>
          <div className="input-wrapper">
            <div className="label">Full Unlock rate</div>
            <Select
              name="fullUnlockRateType"
              label="Type"
              value={plan.fullUnlockRateType || 'FLAT_FEE'}
              onChange={(_, value) =>
                setPlan({ ...plan, fullUnlockRateType: isBillingRateType(value) ? value : plan.fullUnlockRateType })
              }
              options={tierTypes}
              required={false}
            />
            <Select
              name="fullUnlockChargeType"
              label="Charge Type"
              value={plan.fullUnlockChargeType || 'PAY_DIFFERENCE'}
              onChange={(_, value) =>
                setPlan({
                  ...plan,
                  fullUnlockChargeType: isBillingFullUnlockChargeType(value) ? value : plan.fullUnlockChargeType,
                })
              }
              options={fullUnlockChargeTypes}
              required={false}
              disabled={['DISALLOWED', 'TIER_RATE'].includes(plan.fullUnlockRateType)}
            />
            {plan.fullUnlockRateType !== 'TIER_RATE' && plan.fullUnlockRateType !== 'DISALLOWED' ? (
              <CurrencyInput
                onChange={(_, v2) => {
                  setFullUnlockRate(v2);
                }}
                label="$ 0"
                value={fullUnlockRate}
              />
            ) : null}
          </div>
        </div>
        <div className="flex-row flex gap-2 items-top">
          <div className="input-wrapper">
            <div className="label">Enhance on Demand rate</div>
            <Select
              name="enhancedOnDemandRateType"
              label="Type"
              value={plan.enhancedOnDemandRateType || 'FLAT_FEE'}
              onChange={(_, value) =>
                setPlan({
                  ...plan,
                  enhancedOnDemandRateType: isBillingRateType(value) ? value : plan.enhancedOnDemandRateType,
                })
              }
              options={tierTypes}
              required={false}
            />
            {plan.enhancedOnDemandRateType !== 'TIER_RATE' && plan.enhancedOnDemandRateType !== 'DISALLOWED' ? (
              <CurrencyInput
                onChange={(_, v2) => {
                  setEnhancedOnDemandRate(v2);
                }}
                label="$ 0"
                value={enhancedOnDemandRate}
              />
            ) : null}
          </div>
          <div className="input-wrapper">
            <div className="label">Attorney connect rate</div>
            <Select
              name="attorneyConnectFeeRateType"
              label="Type"
              value={plan.attorneyConnectFeeRateType || 'FLAT_FEE'}
              onChange={(_, value) =>
                setPlan({
                  ...plan,
                  attorneyConnectFeeRateType: isBillingRateType(value) ? value : plan.attorneyConnectFeeRateType,
                })
              }
              options={tierTypes}
              required={false}
              disabled={true}
            />
            {plan.attorneyConnectFeeRateType !== 'TIER_RATE' && plan.attorneyConnectFeeRateType !== 'DISALLOWED' ? (
              <CurrencyInput
                onChange={(_, v2) => {
                  setAttorneyConnectFeeRate(v2);
                }}
                label="$ 0"
                value={attorneyConnectFeeRate}
              />
            ) : null}
          </div>
        </div>

        <div className="flex-row flex gap-2 items-top">
          <div className="input-wrapper">
            <div className="label">Attorney invite rate</div>
            <Select
              name="attorneyInviteFeeRateType"
              label="Type"
              value={plan.attorneyInviteFeeRateType || 'FLAT_FEE'}
              onChange={(_, value) =>
                setPlan({
                  ...plan,
                  attorneyInviteFeeRateType: isBillingRateType(value) ? value : plan.attorneyInviteFeeRateType,
                })
              }
              options={tierTypes}
              required={false}
              disabled={true}
            />
            {plan.attorneyInviteFeeRateType !== 'TIER_RATE' && plan.attorneyInviteFeeRateType !== 'DISALLOWED' ? (
              <CurrencyInput
                onChange={(_, v2) => {
                  setAttorneyInviteFeeRate(v2);
                }}
                label="$ 0"
                value={attorneyInviteFeeRate}
              />
            ) : null}
          </div>
          <div className="input-wrapper">
            <div className="label">Rush label rate</div>
            <Select
              name="rushLabelFeeRateType"
              label="Type"
              value={plan.rushLabelFeeRateType || 'FLAT_FEE'}
              onChange={(_, value) =>
                setPlan({ ...plan, rushLabelFeeRateType: isBillingRateType(value) ? value : plan.rushLabelFeeRateType })
              }
              options={tierTypes}
              required={false}
              disabled={true}
            />
            {plan.rushLabelFeeRateType !== 'TIER_RATE' && plan.rushLabelFeeRateType !== 'DISALLOWED' ? (
              <CurrencyInput
                onChange={(_, v2) => {
                  setRushLabelFeeRate(v2);
                }}
                label="$ 0"
                value={rushLabelFeeRate}
              />
            ) : null}
          </div>
        </div>

        <div className="input-wrapper">
          <div className="label">Free trial days</div>
          <Input
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPlan({ ...plan, trialPeriodDays: parseInt(e.target.value, 10) || 0 })
            }
            label="Number of free trial days"
            value={plan.trialPeriodDays}
          />
        </div>

        <div className="input-wrapper">
          <div className="label">Free Seats</div>
          <Input
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPlan({ ...plan, freeSeats: parseInt(e.target.value, 10) || 0 })
            }
            label="Number of free seats in this plan"
            value={plan.freeSeats}
          />
        </div>

        <div className="input-wrapper">
          <div className="label">Audio hours per month</div>
          <Input
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPlan({ ...plan, audioHoursPerMonth: parseInt(e.target.value, 10) || 0 })
            }
            label="Audio hours per month"
            value={plan.audioHoursPerMonth || 0}
            disabled={plan.unlimitedHoursPerMonth}
          />
        </div>

        <div className="form-switch">
          <div className="label">Unlimited Audio Hours</div>
          <Switch
            checked={plan.unlimitedHoursPerMonth}
            onChange={() =>
              setPlan({ ...plan, unlimitedHoursPerMonth: !plan.unlimitedHoursPerMonth, audioHoursPerMonth: 0 })
            }
          />
        </div>

        <div className="input-wrapper">
          <div className="label">In-person capture channels</div>
          <Input
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPlan({ ...plan, inPersonCaptureChannels: parseInt(e.target.value, 10) || 0 })
            }
            label="In-person capture channels"
            value={plan.inPersonCaptureChannels ?? 0}
          />
        </div>

        <div className="form-switch">
          <div className="label">Public plan</div>
          <Switch checked={plan.public} onChange={() => setPlan({ ...plan, public: !plan.public })} />
        </div>

        <div className="form-switch">
          <div className="label">Allowed to choose Transcript Unlock Type</div>
          <Switch
            checked={plan.allowedToChooseTranscriptUnlockType}
            onChange={() =>
              setPlan({ ...plan, allowedToChooseTranscriptUnlockType: !plan.allowedToChooseTranscriptUnlockType })
            }
          />
        </div>

        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button disabled={isSubmitting} onClick={handleSubmit}>
            Save
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
